import axios from 'axios';

// Default to same-origin so CRA's dev proxy (frontend/src/setupProxy.js) can forward /api and /health
// to the backend without requiring CORS during local development.
// For production or non-proxied setups, set REACT_APP_BACKEND_URL explicitly.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const backendClient = axios.create({
  baseURL: BACKEND_URL,
  timeout: 600000, // Increased to 10 minutes for large dataset generation
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface Manufacturer {
  id: string;
  name: string;
  country: string;
  website: string;
  description: string;
  logo_url?: string;
}

export interface MachineType {
  id: string;
  manufacturer_id: string;
  manufacturer_name: string;
  name: string;
  model: string;
  category: string;
  description: string;
  communication_protocols: string[];
}

export interface Sensor {
  id: string;
  machine_type_id: string;
  name: string;
  sensor_type: string;
  unit: string;
  min_value: number;
  max_value: number;
  typical_value: number;
  precision_digits: number;
  sampling_rate_hz: number;
  opc_ua_node_id: string;
  description: string;
}

export interface SimulationProfile {
  id: string;
  machine_type_id: string;
  name: string;
  description: string;
  scenario_type: string;
  duration_seconds: number;
  parameters: Record<string, unknown>;
}

export interface SimulationConfig {
  machineTypeId: string;
  durationSeconds: number;
  resolutionMs: number;
  scenario: 'normal' | 'anomaly' | 'failure' | 'startup' | 'shutdown' | 'corner-case' | 'cyber-physical-attack' | 'cascading-drift' | 'ood-detection';
  format: 'json' | 'json-ld' | 'aasx' | 'csv (vdi 2770)' | 'xml' | 'opcua' | 'mqtt/sparkplug b' | 'parquet';
  // 2025 Digital Twin Standards (ISO 23247 / IDTA)
  fidelity?: string;
  targetSystem?: string;
  anomalyType?: string;
  outputFormats?: string[];
  synthesize?: boolean; // Whether to generate realistic data (true) or schema-only (false)
  anomalyConfig?: {
    type: 'spike' | 'drift' | 'noise' | 'step_change' | 'oscillation' | 'sensor_freeze' | 'out_of_range';
    startTime: number;
    duration: number;
    amplitude: number;
  };
}

export interface SimulationResult {
  id: string;
  generatedAt: string;
  machineType: string;
  manufacturer: string;
  config: SimulationConfig;
  sensors: Sensor[];
  dataPoints: Array<{
    timestamp: string;
    timestampMs: number;
    values: Record<string, number>;
  }>;
  statistics: Record<string, {
    min: number;
    max: number;
    avg: number;
    stdDev: number;
  }>;
}

export interface SimulationInfo {
  durationSeconds: number;
  resolutionMs: number;
  dataPointsCount: number;
  sensorsCount: number;
  estimatedSizeKB: number;
  availableFormats: string[];
  fidelityLevels?: string[];
  targetSystems?: string[];
}

// API Functions

export const getManufacturers = async (): Promise<Manufacturer[]> => {
  const response = await backendClient.get('/api/manufacturers');
  return response.data;
};

export const getMachineTypes = async (manufacturerId?: string, category?: string): Promise<MachineType[]> => {
  const params = new URLSearchParams();
  if (manufacturerId) params.append('manufacturer_id', manufacturerId);
  if (category) params.append('category', category);
  
  const response = await backendClient.get(`/api/machine-types?${params.toString()}`);
  return response.data;
};

export const getMachineType = async (id: string): Promise<MachineType> => {
  const response = await backendClient.get(`/api/machine-types/${id}`);
  return response.data;
};

export const getCategories = async (): Promise<string[]> => {
  const response = await backendClient.get('/api/categories');
  return response.data;
};

export const getSensors = async (machineTypeId?: string): Promise<Sensor[]> => {
  const params = machineTypeId ? `?machine_type_id=${machineTypeId}` : '';
  const response = await backendClient.get(`/api/sensors${params}`);
  return response.data;
};

export const getSimulationProfiles = async (machineTypeId?: string): Promise<SimulationProfile[]> => {
  const params = machineTypeId ? `?machine_type_id=${machineTypeId}` : '';
  const response = await backendClient.get(`/api/simulation-profiles${params}`);
  return response.data;
};

export const getSimulationInfo = async (
  machineTypeId: string,
  durationSeconds: number,
  resolutionMs: number
): Promise<SimulationInfo> => {
  const response = await backendClient.get('/api/simulation/info', {
    params: { machineTypeId, durationSeconds, resolutionMs },
  });
  return response.data;
};

export const generateSimulation = async (config: SimulationConfig): Promise<SimulationResult | string> => {
  const response = await backendClient.post('/api/simulation/generate', config, {
    responseType: config.format === 'json' ? 'json' : 'text',
  });
  return response.data;
};

export const getDocumentation = async (machineTypeId: string): Promise<{
  machineType: MachineType;
  sensors: Sensor[];
  simulationProfiles: SimulationProfile[];
  communicationProtocols: string[];
}> => {
  const response = await backendClient.get(`/api/documentation/${machineTypeId}`);
  return response.data;
};

// Health check
export const checkBackendHealth = async (): Promise<boolean> => {
  try {
    const response = await backendClient.get('/health');
    return response.data.status === 'ok';
  } catch {
    return false;
  }
};

// ============================================================================
// CONTENT HISTORY API
// ============================================================================

export interface ContentHistoryItem {
  id: string;
  user_id: string | null;
  session_id: string | null;
  content_type: string;
  title: string | null;
  input_data: Record<string, unknown>;
  output_data: Record<string, unknown>;
  format: string | null;
  status: string;
  is_favorite: boolean;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface HistoryResponse {
  items: ContentHistoryItem[];
  total: number;
}

export interface ContentTypeCount {
  content_type: string;
  count: number;
}

// Get token from localStorage for authenticated requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('dz_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get session ID for anonymous users
const getSessionId = () => {
  let sessionId = localStorage.getItem('dz_session_id');
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('dz_session_id', sessionId);
  }
  return sessionId;
};

export const getHistory = async (params?: {
  contentType?: string;
  isFavorite?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<HistoryResponse> => {
  const response = await backendClient.get('/api/history', {
    params,
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
  return response.data;
};

export const getHistoryItem = async (id: string): Promise<ContentHistoryItem> => {
  const response = await backendClient.get(`/api/history/${id}`, {
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
  return response.data;
};

export const createHistoryEntry = async (data: {
  contentType: string;
  title?: string;
  inputData: Record<string, unknown>;
  outputData: Record<string, unknown>;
  format?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}): Promise<ContentHistoryItem> => {
  const response = await backendClient.post('/api/history', data, {
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
  return response.data;
};

export const updateHistoryItem = async (
  id: string, 
  updates: { title?: string; isFavorite?: boolean; tags?: string[] }
): Promise<ContentHistoryItem> => {
  const response = await backendClient.patch(`/api/history/${id}`, updates, {
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
  return response.data;
};

export const deleteHistoryItem = async (id: string): Promise<void> => {
  await backendClient.delete(`/api/history/${id}`, {
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
};

export const getContentTypes = async (): Promise<ContentTypeCount[]> => {
  const response = await backendClient.get('/api/history/types', {
    headers: {
      ...getAuthHeaders(),
      'X-Session-Id': getSessionId(),
    },
  });
  return response.data;
};

// ============================================================================
// CUSTOM MACHINES & SENSORS CRUD
// ============================================================================

// Create custom manufacturer
export const createManufacturer = async (data: {
  name: string;
  country?: string;
  website?: string;
  description?: string;
  logo_url?: string;
}): Promise<Manufacturer> => {
  const response = await backendClient.post('/api/manufacturers', data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Update manufacturer
export const updateManufacturer = async (
  id: string,
  data: Partial<Omit<Manufacturer, 'id'>>
): Promise<Manufacturer> => {
  const response = await backendClient.put(`/api/manufacturers/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Delete manufacturer
export const deleteManufacturer = async (id: string): Promise<void> => {
  await backendClient.delete(`/api/manufacturers/${id}`, {
    headers: getAuthHeaders(),
  });
};

// Create custom machine type
export const createMachineType = async (data: {
  manufacturer_id: string;
  name: string;
  model?: string;
  category?: string;
  description?: string;
  image_url?: string;
  datasheet_url?: string;
  communication_protocols?: string[];
}): Promise<MachineType> => {
  const response = await backendClient.post('/api/machine-types', data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Update machine type
export const updateMachineType = async (
  id: string,
  data: Partial<Omit<MachineType, 'id' | 'manufacturer_id' | 'manufacturer_name'>>
): Promise<MachineType> => {
  const response = await backendClient.put(`/api/machine-types/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Delete machine type
export const deleteMachineType = async (id: string): Promise<void> => {
  await backendClient.delete(`/api/machine-types/${id}`, {
    headers: getAuthHeaders(),
  });
};

// Create custom sensor
export const createSensor = async (data: {
  machine_type_id: string;
  name: string;
  sensor_type: string;
  unit: string;
  min_value?: number;
  max_value?: number;
  typical_value?: number;
  precision_digits?: number;
  sampling_rate_hz?: number;
  opc_ua_node_id?: string;
  description?: string;
}): Promise<Sensor> => {
  const response = await backendClient.post('/api/sensors', data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Update sensor
export const updateSensor = async (
  id: string,
  data: Partial<Omit<Sensor, 'id' | 'machine_type_id'>>
): Promise<Sensor> => {
  const response = await backendClient.put(`/api/sensors/${id}`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Delete sensor
export const deleteSensor = async (id: string): Promise<void> => {
  await backendClient.delete(`/api/sensors/${id}`, {
    headers: getAuthHeaders(),
  });
};

// Get sensor types for dropdown
export const getSensorTypes = async (): Promise<string[]> => {
  const response = await backendClient.get('/api/sensor-types');
  return response.data;
};
